import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RatingStars } from "@/components/RatingStars";
import { WaitlistModal } from "@/components/WaitlistModal";

const CATS = ["Esperienze", "Digitali", "Arte Originale", "Merchandising"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [buying, setBuying] = useState(null);
  const [reviews, setReviews] = useState({});
  const [wlProduct, setWlProduct] = useState(null);
  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const productsByCat = useMemo(() => {
    const map = {};
    for (const cat of CATS) map[cat] = products.filter((p) => p.category === cat);
    return map;
  }, [products]);

  const buy = async (p) => {
    setBuying(p.id);
    try {
      const { data } = await api.post("/shop/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Errore nell'avvio del pagamento, riprova.");
      setBuying(null);
    }
  };

  return (
    <div data-testid="shop-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Shop</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
            Esperienze, arte<br />e mondo Wildersquad
          </h1>
          <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl">
            Cene esclusive, corsi digitali, opere e riproduzioni di Italo Blandolino, merchandising in edizione limitata.
          </p>
        </div>
      </section>

      {CATS.map((cat) => (
        <section key={cat} className="border-b border-[#2A2A2A]">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
            <h2 className="font-display text-lg font-bold mb-8" data-testid={`shop-cat-${cat}`}>{cat}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
              {productsByCat[cat].map((p) => (
                <article key={p.id} className="bg-[#0A0A0A] flex flex-col" data-testid={`shop-product-${p.id}`}>
                  <div className="art-frame aspect-[4/3]">
                    <img src={p.image} alt={p.name} loading="lazy" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl font-bold leading-snug mb-2">{p.name}</h3>
                    <div className="mb-1"><RatingStars rating={reviews[p.id]} /></div>
                    <p className="text-sm text-[#C9C4B8] leading-relaxed mb-5 flex-1">{p.desc}</p>
                    {cat === "Esperienze" ? (
                      <>
                        <p className="font-display text-3xl font-black text-[#E4BD44] mb-1" data-testid={`shop-price-${p.id}`}>
                          {p.price ? `€${p.price.toLocaleString("it-IT")}` : p.price_label}
                        </p>
                        <span className="mb-3" />
                        <div className="flex flex-wrap gap-2">
                          {p.price && (
                            <button onClick={() => buy(p)} disabled={buying === p.id}
                               className="btn-primary !py-2.5 !px-5 text-xs disabled:opacity-60" data-testid={`shop-buy-${p.id}`}>
                              {buying === p.id ? "Un attimo…" : "Acquista Ora"}
                            </button>
                          )}
                          {(p.info || !p.price) && (
                            <a href={`https://wa.me/393284432930?text=${encodeURIComponent(`Ciao Wildersquad, vorrei info su ${p.name}`)}`}
                               target="_blank" rel="noopener noreferrer"
                               className="btn-outline !py-2.5 !px-5 text-xs" data-testid={`shop-info-${p.id}`}>
                              Richiedi Info
                            </a>
                          )}
                        </div>
                      </>
                    ) : (
                      <button onClick={() => setWlProduct(p)}
                        className="btn-primary !py-2.5 !px-5 text-xs self-start" data-testid={`shop-waitlist-${p.id}`}>
                        RICHIEDI INFO
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ))}

      <WaitlistModal product={wlProduct || {}} open={!!wlProduct} onOpenChange={(v) => !v && setWlProduct(null)} />

      <section>
        <div className="max-w-4xl mx-auto px-5 lg:px-10 py-10">
          <p className="text-[10px] text-[#9a9484] leading-relaxed">
            © Tutti i diritti riservati. Immagini e contenuti protetti da diritto d'autore: vietato il riutilizzo
            senza licenza e royalty concordate con Wildersquad. Per consulenze e opere originali ti ricontattiamo entro 24 ore.
          </p>
        </div>
      </section>
    </div>
  );
}
